import { useEffect, useState, useCallback } from 'react';
import api, { formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Crown, Plus, Building2, Users, Boxes, Ticket, FolderArchive, RefreshCw,
  Search, Pause, Play, Trash2, Copy, CheckCircle2, AlertTriangle, ShieldCheck,
  Settings,
} from 'lucide-react';
import TenantDetailDrawer from '../components/TenantDetailDrawer';
import PlansManagerCard from '../components/PlansManagerCard';

const SEVERITY_DOT = {
  info: 'bg-blue-500',
  warning: 'bg-amber-500',
  error: 'bg-red-500',
};

export default function SuperAdmin() {
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [busyTid, setBusyTid] = useState(null);
  const [revealedCreds, setRevealedCreds] = useState(null);
  const [drawerTid, setDrawerTid] = useState(null);
  const [dash, setDash] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, sRes, aRes, dRes] = await Promise.all([
        api.get('/super-admin/tenants'),
        api.get('/super-admin/tenants/_/platform-stats'),
        api.get('/super-admin/tenants/_/audit?limit=20').catch(() => ({ data: [] })),
        api.get('/super-admin/tenants/_/platform-dashboard').catch(() => ({ data: null })),
      ]);
      setTenants(tRes.data || []);
      setStats(sRes.data || null);
      setAudit(aRes.data || []);
      setDash(dRes.data || null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Failed to load platform data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visibleTenants = tenants.filter(t => t.status !== 'deleted');
  const filtered = visibleTenants.filter(t => {
    if (!search) return true;
    const q = search.toLowerCase();
    return t.id?.toLowerCase().includes(q) ||
           t.company_name?.toLowerCase().includes(q) ||
           t.company_domain?.toLowerCase().includes(q);
  });

  const toggleStatus = async (t, target) => {
    setBusyTid(t.id);
    try {
      await api.post(`/super-admin/tenants/${t.id}/${target}`);
      toast.success(target === 'suspend' ? `${t.company_name} suspended` : `${t.company_name} reactivated`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyTid(null); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Soft-delete tenant "${t.company_name}"? Data is preserved but the tenant disappears from this list.`)) return;
    setBusyTid(t.id);
    try {
      await api.delete(`/super-admin/tenants/${t.id}`);
      toast.success(`Tenant ${t.company_name} deleted`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyTid(null); }
  };

  return (
    <div className="space-y-6" data-testid="super-admin-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-rose-500 text-white flex items-center justify-center shadow-sm">
            <Crown className="w-6 h-6" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-amber-700">Platform</div>
            <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight mt-0.5">Super Admin Console</h1>
            <p className="text-sm text-slate-500 mt-1">Manage every tenant on this Lanvaus instance — create, suspend, monitor.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading} className="rounded-full" data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button onClick={() => setCreateOpen(true)} data-testid="create-tenant-btn"
                  className="rounded-full bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-600 hover:to-rose-600 text-white">
            <Plus className="w-4 h-4 mr-1.5" /> Create tenant
          </Button>
        </div>
      </div>

      {/* Business metrics — MRR + activity + plan distribution */}
      {dash && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="business-metrics">
          <div className="bg-gradient-to-br from-amber-50 to-rose-50 border border-amber-200 rounded-2xl p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-700 font-bold">Monthly recurring revenue</div>
            <div className="font-heading text-3xl font-bold text-slate-900 mt-1 tabular-nums">₹{(dash.mrr.amount_inr || 0).toLocaleString('en-IN')}</div>
            <div className="text-[11px] text-slate-500 mt-1">{dash.mrr.billing_enabled_tenants}/{dash.mrr.tenants_total} tenants billed · {(['starter','professional','enterprise']).map(p => `${p[0].toUpperCase()} ₹${dash.mrr.plan_prices_inr[p].toLocaleString('en-IN')}`).join(' · ')}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Activity (30d)</div>
            <div className="grid grid-cols-3 gap-2 mt-2">
              <div><div className="text-xl font-bold tabular-nums text-emerald-700">{dash.activity_30d.new_tenants}</div><div className="text-[10px] text-slate-500">new tenants</div></div>
              <div><div className="text-xl font-bold tabular-nums text-blue-700">{dash.activity_30d.new_tenants_7d}</div><div className="text-[10px] text-slate-500">last 7 days</div></div>
              <div><div className="text-xl font-bold tabular-nums text-orange-700">{dash.activity_30d.suspensions}</div><div className="text-[10px] text-slate-500">suspensions</div></div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Plan distribution</div>
            <div className="space-y-1.5 mt-2">
              {Object.entries(dash.plan_distribution).filter(([_, n]) => n > 0).map(([p, n]) => (
                <div key={p} className="flex items-center gap-2 text-xs">
                  <div className="w-20 capitalize">{p}</div>
                  <div className="flex-1 bg-slate-100 h-2 rounded-full overflow-hidden"><div className={`h-full ${p === 'enterprise' ? 'bg-amber-500' : p === 'professional' ? 'bg-blue-500' : 'bg-slate-400'}`} style={{ width: `${(n / Math.max(1, dash.mrr.tenants_total)) * 100}%` }} /></div>
                  <div className="w-6 text-right font-semibold tabular-nums">{n}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="platform-stats">
        <StatCard icon={Building2} label="Tenants" value={stats?.total_tenants} color="amber" tid="stat-total" />
        <StatCard icon={CheckCircle2} label="Active" value={stats?.active_tenants} color="emerald" tid="stat-active" />
        <StatCard icon={Pause} label="Suspended" value={stats?.suspended_tenants} color="orange" tid="stat-suspended" />
        <StatCard icon={Users} label="Users" value={stats?.total_employees} color="blue" tid="stat-users" />
        <StatCard icon={Boxes} label="Assets" value={stats?.total_assets} color="indigo" tid="stat-assets" />
        <StatCard icon={Ticket} label="Tickets" value={stats?.total_tickets} color="violet" tid="stat-tickets" />
        <StatCard icon={FolderArchive} label="Reports" value={stats?.total_reports_vaulted} color="slate" tid="stat-reports" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        {/* Tenants table */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-[#3D4A8E]" />
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-600">Tenants</div>
              <span className="text-xs text-slate-400">· {filtered.length} of {visibleTenants.length}</span>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                     placeholder="Search by name, id, domain…"
                     data-testid="tenant-search"
                     className="pl-8 h-8 text-xs rounded-full w-64" />
            </div>
          </div>
          <table className="w-full text-sm" data-testid="tenants-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.15em] text-slate-500 font-bold">
              <tr>
                <th className="text-left px-5 py-2.5">Tenant</th>
                <th className="text-left py-2.5">Plan</th>
                <th className="text-left py-2.5">Status</th>
                <th className="text-right py-2.5">Users</th>
                <th className="text-left py-2.5 pl-4">Created</th>
                <th className="text-right px-5 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && tenants.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-400 text-xs">Loading tenants…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-400 text-xs">No tenants match your search.</td></tr>
              )}
              {filtered.map(t => {
                const tStats = (stats?.by_tenant || []).find(b => b.tenant_id === t.id);
                const isDefault = t.id === 'lanvaus';
                return (
                  <tr key={t.id} className="hover:bg-slate-50 cursor-pointer" data-testid={`tenant-row-${t.id}`}
                      onClick={() => setDrawerTid(t.id)}>
                    <td className="px-5 py-3">
                      <div className="font-semibold text-slate-900 hover:text-amber-700 transition-colors">{t.company_name}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        <code className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">{t.id}</code>
                        {t.company_domain && <> · {t.company_domain}</>}
                      </div>
                    </td>
                    <td className="py-3 text-xs">
                      <span className={`inline-block px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider ${
                        t.subscription_plan === 'enterprise' ? 'bg-amber-100 text-amber-700' :
                        t.subscription_plan === 'professional' ? 'bg-blue-100 text-blue-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>{t.subscription_plan || 'starter'}</span>
                    </td>
                    <td className="py-3">
                      <StatusBadge status={t.status} testid={`status-${t.id}`} />
                    </td>
                    <td className="py-3 text-right tabular-nums font-medium text-slate-700">{tStats?.employees ?? '—'}</td>
                    <td className="py-3 pl-4 text-xs text-slate-500">{t.created_at?.slice(0, 10) || '—'}</td>
                    <td className="px-5 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setDrawerTid(t.id)}
                                data-testid={`configure-${t.id}`}
                                className="h-7 px-2 text-[#3D4A8E] hover:bg-indigo-50">
                          <Settings className="w-3.5 h-3.5" />
                        </Button>
                        {t.status === 'active' && !isDefault && (
                          <Button size="sm" variant="ghost" onClick={() => toggleStatus(t, 'suspend')}
                                  disabled={busyTid === t.id}
                                  data-testid={`suspend-${t.id}`}
                                  className="h-7 px-2 text-orange-700 hover:bg-orange-50">
                            <Pause className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {t.status === 'suspended' && (
                          <Button size="sm" variant="ghost" onClick={() => toggleStatus(t, 'reactivate')}
                                  disabled={busyTid === t.id}
                                  data-testid={`reactivate-${t.id}`}
                                  className="h-7 px-2 text-emerald-700 hover:bg-emerald-50">
                            <Play className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {!isDefault && (
                          <Button size="sm" variant="ghost" onClick={() => remove(t)}
                                  disabled={busyTid === t.id}
                                  data-testid={`delete-${t.id}`}
                                  className="h-7 px-2 text-red-700 hover:bg-red-50">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {isDefault && (
                          <span className="text-[10px] text-slate-400 italic">default · protected</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Audit side panel */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="audit-panel">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="w-4 h-4 text-[#3D4A8E]" />
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-600">Recent platform actions</div>
          </div>
          {audit.length === 0 ? (
            <div className="text-xs text-slate-400 italic">No actions logged yet.</div>
          ) : (
            <ul className="space-y-2.5">
              {audit.map((a, i) => (
                <li key={i} className="flex gap-2.5 text-xs">
                  <span className={`w-2 h-2 mt-1.5 rounded-full flex-shrink-0 ${SEVERITY_DOT[a.severity] || 'bg-slate-300'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-700">{(a.action || '').replace('super_admin.', '').replace(/_/g, ' ')}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      <code className="font-mono bg-slate-50 px-1 rounded">{a.target_id || a.tenant_id}</code>
                      {a.actor_name && <> · by {a.actor_name}</>}
                    </div>
                    <div className="text-[10px] text-slate-400">{a.ts?.replace('T', ' ').slice(0, 19)}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <CreateTenantDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(creds) => { load(); if (creds) setRevealedCreds(creds); }}
      />

      <RevealedCredentialsDialog creds={revealedCreds} onClose={() => setRevealedCreds(null)} />

      <PlansManagerCard onChanged={load} />

      <TenantDetailDrawer
        tenantId={drawerTid}
        open={!!drawerTid}
        onClose={() => setDrawerTid(null)}
        onChanged={load}
      />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, tid }) {
  const palette = {
    amber: 'bg-amber-50 text-amber-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    orange: 'bg-orange-50 text-orange-700',
    blue: 'bg-blue-50 text-blue-700',
    indigo: 'bg-indigo-50 text-indigo-700',
    violet: 'bg-violet-50 text-violet-700',
    slate: 'bg-slate-100 text-slate-700',
  }[color] || 'bg-slate-50 text-slate-700';
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-3" data-testid={tid}>
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${palette}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-bold">{label}</div>
      </div>
      <div className="font-heading text-2xl font-bold text-slate-900 mt-2 tabular-nums">{value ?? '—'}</div>
    </div>
  );
}

function StatusBadge({ status, testid }) {
  const m = {
    active:    { label: 'Active',    cls: 'bg-emerald-100 text-emerald-700' },
    suspended: { label: 'Suspended', cls: 'bg-orange-100 text-orange-700' },
    deleted:   { label: 'Deleted',   cls: 'bg-red-100 text-red-700' },
  }[status] || { label: status, cls: 'bg-slate-100 text-slate-700' };
  return (
    <span data-testid={testid} className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${m.cls}`}>
      {m.label}
    </span>
  );
}

function CreateTenantDialog({ open, onClose, onCreated }) {
  const initialForm = {
    company_name: '', company_domain: '', subscription_plan: 'starter',
    industry: '', company_address: '',
    initial_admin_email: '', initial_admin_name: '',
    use_custom_plan: false,
    custom_plan: { name: 'Custom plan', price_inr: 0, max_users: 50, storage_gb: 100, modules: [] },
  };
  const [form, setForm] = useState(initialForm);
  const [busy, setBusy] = useState(false);
  const [plans, setPlans] = useState([]);
  const [allModules, setAllModules] = useState([]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [p, m] = await Promise.all([
          api.get('/super-admin/plans'),
          api.get('/super-admin/plans/available-modules').catch(() => ({ data: { modules: [] } })),
        ]);
        setPlans((p.data || []).filter(x => !x.is_custom));
        setAllModules(m.data?.modules || []);
      } catch (e) { /* default */ }
    })();
  }, [open]);

  const toggleModule = (mod) => {
    const cur = form.custom_plan.modules || [];
    const next = cur.includes(mod) ? cur.filter(x => x !== mod) : [...cur, mod];
    setForm({ ...form, custom_plan: { ...form.custom_plan, modules: next } });
  };

  const submit = async () => {
    if (!form.company_name.trim()) { toast.error('Company name is required'); return; }
    setBusy(true);
    try {
      const body = {
        company_name: form.company_name, company_domain: form.company_domain,
        industry: form.industry, company_address: form.company_address,
        initial_admin_email: form.initial_admin_email,
        initial_admin_name: form.initial_admin_name,
      };
      if (form.use_custom_plan) {
        body.custom_plan = {
          ...form.custom_plan,
          price_inr: parseInt(form.custom_plan.price_inr, 10) || 0,
          max_users: form.custom_plan.max_users ? parseInt(form.custom_plan.max_users, 10) : null,
          storage_gb: parseInt(form.custom_plan.storage_gb, 10) || 50,
        };
      } else {
        body.subscription_plan = form.subscription_plan;
      }
      const { data } = await api.post('/super-admin/tenants', body);
      toast.success(`Tenant ${data.company_name} created`);
      onClose(); setForm(initialForm);
      onCreated(data.initial_admin);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-tenant-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">Create a new tenant</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-xs font-semibold text-slate-600">Company name *</Label>
            <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                   data-testid="form-company-name" placeholder="Acme Inc." className="rounded-xl mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-slate-600">Primary domain</Label>
              <Input value={form.company_domain} onChange={(e) => setForm({ ...form, company_domain: e.target.value })}
                     data-testid="form-company-domain" placeholder="acme.com" className="rounded-xl mt-1" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Industry</Label>
              <Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })}
                     data-testid="form-industry" placeholder="Technology, Healthcare, etc." className="rounded-xl mt-1" />
            </div>
          </div>

          {/* Plan picker */}
          <div className="border-t border-slate-100 pt-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Plan</div>
              <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                <input type="checkbox" checked={form.use_custom_plan}
                       onChange={(e) => setForm({ ...form, use_custom_plan: e.target.checked })}
                       data-testid="use-custom-plan" />
                Customise plan for this tenant
              </label>
            </div>
            {!form.use_custom_plan ? (
              <div className="grid grid-cols-3 gap-2" data-testid="plan-picker">
                {plans.map(p => (
                  <button type="button" key={p.id}
                          onClick={() => setForm({ ...form, subscription_plan: p.id })}
                          data-testid={`pick-plan-${p.id}`}
                          className={`text-left rounded-xl border-2 p-3 transition-all ${form.subscription_plan === p.id ? 'border-amber-500 bg-amber-50/50' : 'border-slate-200 hover:border-slate-300'}`}>
                    <div className="font-bold text-slate-900">{p.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">₹{(p.price_inr || 0).toLocaleString('en-IN')}/mo</div>
                    <div className="text-[10px] text-slate-400 mt-1">
                      {p.max_users ? `${p.max_users} users` : 'Unlimited users'} · {p.storage_gb} GB
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="bg-amber-50/40 border border-amber-200 rounded-xl p-3 space-y-3" data-testid="custom-plan-form">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Plan name</Label>
                    <Input value={form.custom_plan.name} onChange={(e) => setForm({ ...form, custom_plan: { ...form.custom_plan, name: e.target.value } })} data-testid="cp-name" className="rounded-lg mt-1" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Price /mo (₹)</Label>
                    <Input type="number" value={form.custom_plan.price_inr} onChange={(e) => setForm({ ...form, custom_plan: { ...form.custom_plan, price_inr: e.target.value } })} data-testid="cp-price" className="rounded-lg mt-1" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Max users (blank = unlimited)</Label>
                    <Input type="number" value={form.custom_plan.max_users || ''} onChange={(e) => setForm({ ...form, custom_plan: { ...form.custom_plan, max_users: e.target.value } })} data-testid="cp-users" className="rounded-lg mt-1" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Storage (GB)</Label>
                    <Input type="number" value={form.custom_plan.storage_gb} onChange={(e) => setForm({ ...form, custom_plan: { ...form.custom_plan, storage_gb: e.target.value } })} data-testid="cp-storage" className="rounded-lg mt-1" />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Modules included</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {allModules.map(m => {
                      const on = (form.custom_plan.modules || []).includes(m);
                      return (
                        <button type="button" key={m} onClick={() => toggleModule(m)}
                                data-testid={`cp-mod-${m}`}
                                className={`text-xs px-2.5 py-1 rounded-full border-2 transition-colors ${on ? 'border-amber-500 bg-amber-100 text-amber-800 font-semibold' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                          {m}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-slate-100 pt-3 space-y-3">
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">First tenant-admin (optional)</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-slate-600">Admin email</Label>
                <Input value={form.initial_admin_email} onChange={(e) => setForm({ ...form, initial_admin_email: e.target.value })}
                       data-testid="form-admin-email" placeholder="admin@acme.com" className="rounded-xl mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">Admin name</Label>
                <Input value={form.initial_admin_name} onChange={(e) => setForm({ ...form, initial_admin_name: e.target.value })}
                       data-testid="form-admin-name" placeholder="Jane Doe" className="rounded-xl mt-1" />
              </div>
            </div>
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-full">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="submit-create-tenant"
                  className="rounded-full bg-gradient-to-r from-amber-500 to-rose-500 text-white">
            {busy ? 'Creating…' : 'Create tenant'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RevealedCredentialsDialog({ creds, onClose }) {
  if (!creds) return null;
  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success('Copied to clipboard'); };
  return (
    <Dialog open={!!creds} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="revealed-creds-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">Tenant admin provisioned 🔐</DialogTitle></DialogHeader>
        {creds.welcome_email_sent ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-xs text-emerald-900 mt-2 flex items-start gap-2" data-testid="email-sent-banner">
            <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>A <b>welcome email with credentials and the login link</b> has been sent to <b>{creds.email}</b>. The details below are a backup copy.</div>
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900 mt-2 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>We couldn&apos;t send the welcome email automatically — share these credentials with the new admin yourself. They&apos;ll be asked to change the password on first login.</div>
          </div>
        )}
        <div className="space-y-3 mt-3">
          <Row label="Email" value={creds.email} onCopy={() => copy(creds.email)} testid="creds-email" />
          <Row label="Temporary password" value={creds.temp_password} onCopy={() => copy(creds.temp_password)} testid="creds-password" />
        </div>
        <DialogFooter><Button onClick={onClose} className="rounded-full" data-testid="creds-close">Got it</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, onCopy, testid }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-center justify-between gap-2" data-testid={testid}>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-bold">{label}</div>
        <code className="font-mono text-sm text-slate-900 break-all">{value}</code>
      </div>
      <Button size="sm" variant="ghost" onClick={onCopy} className="rounded-full"><Copy className="w-3.5 h-3.5" /></Button>
    </div>
  );
}
